import React, { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import { Upload as UploadIcon } from "@mui/icons-material";
import { uploadSheet } from "../../../../api/sheet";

export interface SheetData {
  file_name?: string;
  columns?: string[];
  items?: Record<string, unknown>[];
}

export interface MasterFileUploadSectionProps {
  batchId: string;
  hasExistingFile: boolean;
  fileName?: string;
  isProcessing?: boolean;
  processingMessage?: string;
  sheetData?: SheetData | null;
  onUploadSuccess: () => Promise<void>;
  onContinue?: () => void;
}

export const MasterFileUploadSection: React.FC<
  MasterFileUploadSectionProps
> = ({
  batchId,
  hasExistingFile,
  fileName,
  isProcessing = false,
  processingMessage,
  sheetData,
  onUploadSuccess,
  onContinue,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtensions = [".csv", ".xls", ".xlsx"];
    const fileExtension = file.name
      .toLowerCase()
      .slice(file.name.lastIndexOf("."));

    if (!validExtensions.includes(fileExtension)) {
      setError("Invalid file type. Please upload a CSV, XLS, or XLSX file.");
      return;
    }

    setUploading(true);
    setError(null);
    setUploadSuccess(false);

    try {
      await uploadSheet(batchId, file);
      setUploadSuccess(true);
      setError(null);
      await onUploadSuccess();
    } catch (err) {
      const error = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to upload file. Please check the format and try again."
      );
      setUploadSuccess(false);
    } finally {
      setUploading(false);
    }
  };

  const hasPreviewData = sheetData?.columns && sheetData?.items;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Show requirements and sample only when no data is uploaded */}
      {!hasPreviewData && (
        <>
          {/* Requirements info */}
          <Alert severity="info">
            <AlertTitle sx={{ fontWeight: "bold" }}>
              File Requirements
            </AlertTitle>
            <Typography variant="body2" component="div">
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                <li>
                  The <strong>first column must be the SKU</strong> and it must
                  match your image filenames (e.g., SKU "PROD_001" matches
                  "PROD_001_FRONT.jpg")
                </li>
                <li>Accepted formats: CSV, XLS, XLSX</li>
                <li>
                  You can include any additional columns with product
                  information
                </li>
              </ul>
            </Typography>
          </Alert>

          {/* Sample format */}
          <Box>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Sample Format
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: 200 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell>
                      <strong>SKU</strong>
                    </TableCell>
                    <TableCell>
                      <strong>name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>composition</strong>
                    </TableCell>
                    <TableCell>
                      <strong>country</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      PROD_001
                    </TableCell>
                    <TableCell>Product Name 1</TableCell>
                    <TableCell>100% Cotton</TableCell>
                    <TableCell>Italy</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      PROD_002
                    </TableCell>
                    <TableCell>Product Name 2</TableCell>
                    <TableCell>80% Wool</TableCell>
                    <TableCell>France</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}

      {/* Processing state */}
      {isProcessing && (
        <Alert severity="info" icon={<CircularProgress size={20} />}>
          <AlertTitle>Processing</AlertTitle>
          <Typography variant="body2">
            {processingMessage || "Processing your file..."}
          </Typography>
        </Alert>
      )}

      {/* Current file info - show when file exists or after successful upload */}
      {(hasExistingFile || uploadSuccess) && !isProcessing && !uploading && (
        <Alert severity="success">
          <AlertTitle>Master File Uploaded</AlertTitle>
          <Typography variant="body2">
            <strong>{fileName || "Master file"}</strong> is ready.
            {hasPreviewData &&
              ` ${sheetData?.items?.length || 0} rows, ${
                sheetData?.columns?.length || 0
              } columns.`}{" "}
            {onContinue
              ? "You can continue to settings or upload a different file."
              : "You can upload a different file if needed."}
          </Typography>
        </Alert>
      )}

      {/* Preview table when data is available (hide during upload) */}
      {hasPreviewData && !isProcessing && !uploading && (
        <Box>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Data Preview
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 400, overflow: "auto" }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {sheetData?.columns?.map((column, idx) => (
                    <TableCell
                      key={column}
                      sx={{
                        backgroundColor: "action.hover",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        ...(idx === 0 && { fontFamily: "monospace" }),
                      }}
                    >
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sheetData?.items?.map((item, rowIdx) => (
                  <TableRow key={rowIdx} hover>
                    {sheetData?.columns?.map((column, colIdx) => (
                      <TableCell
                        key={`${rowIdx}-${column}`}
                        sx={{
                          whiteSpace: "nowrap",
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          ...(colIdx === 0 && { fontFamily: "monospace" }),
                        }}
                      >
                        {item[column] != null ? String(item[column]) : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Upload button */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <input
          accept=".csv,.xls,.xlsx"
          style={{ display: "none" }}
          id="master-file-upload-stepper"
          type="file"
          onChange={handleFileChange}
          disabled={uploading || isProcessing}
        />
        {hasExistingFile && !isProcessing ? (
          <>
            {!uploading && onContinue && (
              <Button variant="contained" onClick={onContinue} size="large">
                Continue to Settings
              </Button>
            )}
            <label htmlFor="master-file-upload-stepper">
              <Button
                variant={onContinue ? "outlined" : "contained"}
                component="span"
                startIcon={
                  uploading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <UploadIcon />
                  )
                }
                disabled={uploading}
                size="large"
              >
                {uploading ? "Uploading..." : "Upload Different File"}
              </Button>
            </label>
          </>
        ) : !isProcessing ? (
          <label htmlFor="master-file-upload-stepper">
            <Button
              variant="contained"
              component="span"
              startIcon={
                uploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <UploadIcon />
                )
              }
              disabled={uploading}
              size="large"
            >
              {uploading ? "Uploading..." : "Upload Master File"}
            </Button>
          </label>
        ) : null}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          <AlertTitle>Upload Failed</AlertTitle>
          {error}
        </Alert>
      )}
    </Box>
  );
};
